"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { FormScopedContextProvider, useFormContext } from "../__chunks/PQKUBRVU.js";
import "../__chunks/G4PVPWEW.js";
import { createElement, createHook, forwardRef } from "../__chunks/7FZLUSKW.js";
import { useEvent, useInitialValue, useMergeRefs, useTagName, useUpdateEffect, useWrapElement } from "../__chunks/HNHAUIIY.js";
import "../__chunks/SK3NAZA3.js";
import { __objRest, __spreadProps, __spreadValues } from "../__chunks/3YLGPPWQ.js";

// src/form/form.tsx
import { isTextField } from "@ariakit/core/utils/dom";
import { invariant } from "@ariakit/core/utils/misc";
import { useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "form";
function isField(element, items) {
  return items.some(function (item) {
    return item.type === "field" && item.element === element;
  });
}
function getFirstInvalidField(items) {
  return items.find(function (item) {
    var _a;
    return item.type === "field" && ((_a = item.element) == null ? void 0 : _a.getAttribute("aria-invalid")) === "true";
  });
}
var useForm = createHook(function useForm2(_a) {
  var _b = _a,
    store = _b.store,
    _b$validateOnChange = _b.validateOnChange,
    validateOnChange = _b$validateOnChange === void 0 ? true : _b$validateOnChange,
    _b$validateOnBlur = _b.validateOnBlur,
    validateOnBlur = _b$validateOnBlur === void 0 ? true : _b$validateOnBlur,
    _b$resetOnUnmount = _b.resetOnUnmount,
    resetOnUnmount = _b$resetOnUnmount === void 0 ? false : _b$resetOnUnmount,
    _b$resetOnSubmit = _b.resetOnSubmit,
    resetOnSubmit = _b$resetOnSubmit === void 0 ? true : _b$resetOnSubmit,
    _b$autoFocusOnSubmit = _b.autoFocusOnSubmit,
    autoFocusOnSubmit = _b$autoFocusOnSubmit === void 0 ? true : _b$autoFocusOnSubmit,
    props = __objRest(_b, ["store", "validateOnChange", "validateOnBlur", "resetOnUnmount", "resetOnSubmit", "autoFocusOnSubmit"]);
  var context = useFormContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "Form must receive a `store` prop or be wrapped in a FormProvider component.");
  var ref = useRef(null);
  var values = store.useState("values");
  var submitSucceed = store.useState("submitSucceed");
  var submitFailed = store.useState("submitFailed");
  var items = store.useState("items");
  var defaultValues = useInitialValue(values);
  useEffect(function () {
    return resetOnUnmount ? store == null ? void 0 : store.reset : void 0;
  }, [resetOnUnmount, store]);
  useUpdateEffect(function () {
    if (!validateOnChange) return;
    if (values === defaultValues) return;
    store == null ? void 0 : store.validate();
  }, [validateOnChange, values, defaultValues, store]);
  useEffect(function () {
    if (!resetOnSubmit) return;
    if (!submitSucceed) return;
    store == null ? void 0 : store.reset();
  }, [resetOnSubmit, submitSucceed, store]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    shouldFocusOnSubmit = _useState2[0],
    setShouldFocusOnSubmit = _useState2[1];
  useEffect(function () {
    if (!shouldFocusOnSubmit) return;
    if (!submitFailed) return;
    var field = getFirstInvalidField(items);
    var element = field == null ? void 0 : field.element;
    if (!element) return;
    setShouldFocusOnSubmit(false);
    element.focus();
    if (isTextField(element)) {
      element.select();
    }
  }, [autoFocusOnSubmit, submitFailed, items]);
  var onSubmitProp = props.onSubmit;
  var onSubmit = useEvent(function (event) {
    onSubmitProp == null ? void 0 : onSubmitProp(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    store == null ? void 0 : store.submit();
    if (!autoFocusOnSubmit) return;
    setShouldFocusOnSubmit(true);
  });
  var onBlurProp = props.onBlur;
  var onBlur = useEvent(function (event) {
    onBlurProp == null ? void 0 : onBlurProp(event);
    if (event.defaultPrevented) return;
    if (!validateOnBlur) return;
    if (!store) return;
    if (!isField(event.target, store.getState().items)) return;
    store.validate();
  });
  var onResetProp = props.onReset;
  var onReset = useEvent(function (event) {
    onResetProp == null ? void 0 : onResetProp(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    store == null ? void 0 : store.reset();
  });
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(FormScopedContextProvider, {
      value: store,
      children: element
    });
  }, [store]);
  var tagName = useTagName(ref, TagName);
  props = __spreadProps(__spreadValues({
    role: tagName !== "form" ? "form" : void 0,
    noValidate: true
  }, props), {
    ref: useMergeRefs(ref, props.ref),
    onSubmit: onSubmit,
    onBlur: onBlur,
    onReset: onReset
  });
  return props;
});
var Form = forwardRef(function Form2(props) {
  var htmlProps = useForm(props);
  return createElement(TagName, htmlProps);
});
export { Form, useForm };